import React, { useState } from 'react';
import { User, Mail, Phone, KeyRound, Check, AlertCircle, Save } from 'lucide-react';
import api from '../services/api';

const PersonalProfileTab = ({ user, role, onSaved }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        first_name: user?.name?.split(' ')[0] || '',
        last_name: user?.name?.split(' ').slice(1).join(' ') || '',
        phone: user?.phone || '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');

    const [resetLoading, setResetLoading] = useState(false);
    const [resetMessage, setResetMessage] = useState('');

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage('');
        try {
            // Backend expects these fields
            const apiPath = role === 'sourcing_partner' ? '/sourcing/profile' : '/recruiting/profile';
            // Pass the name fields, the backend UpdateProfile trims and interpolates
            await api.put(apiPath, {
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone,
                // Carry over existing fields so we don't nullify Company data since they share a table
                company_name: user?.company_name || '',
                address: user?.address || '',
                legal_entity_name: user?.legal_entity_name || '',
                trade_name: user?.trade_name || '',
                company_website: user?.company_website || '',
                year_of_establishment: user?.year_of_establishment || '',
                company_size: user?.company_size || '',
                specialization: user?.specialization || '',
                account_manager_name: user?.account_manager_name || '',
                official_email: user?.official_email || '',
                direct_phone: user?.direct_phone || '',
                tax_id: user?.tax_id || '',
            });
            setMessage('Personal profile updated successfully');
            if (onSaved) onSaved();
            setIsEditing(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        setResetLoading(true);
        setResetMessage('');
        try {
            await api.post('/auth/forgot-password', { email: user.email });
            setResetMessage('Password reset link sent to your email.');
        } catch (err) {
            setResetMessage('Failed to trigger password reset. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px]">
            <div className="p-8 border-b border-slate-100 bg-slate-50 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
                        <div className="bg-cyan-100 p-2.5 rounded-xl text-cyan-600">
                            <User className="h-6 w-6" />
                        </div>
                        My Profile
                    </h2>
                    <p className="text-slate-500 mt-2 text-sm font-medium">Update your personal account details</p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="px-6 py-2.5 bg-cyan-600 text-white font-bold rounded-xl hover:bg-cyan-700 shadow-md hover:shadow-lg transition-all"
                    >
                        Edit Profile
                    </button>
                )}
            </div>

            <div className="p-8 max-w-2xl">
                {error && (
                    <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3 text-sm font-medium border border-red-100">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        {error}
                    </div>
                )}
                {message && (
                    <div className="mb-6 bg-green-50 text-green-700 p-4 rounded-xl flex items-center gap-3 text-sm font-medium border border-green-100">
                        <Check className="h-5 w-5 shrink-0" />
                        {message}
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">First Name</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all font-medium text-slate-800"
                                />
                            ) : (
                                <p className="text-slate-800 font-semibold px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">{formData.first_name}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Last Name</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all font-medium text-slate-800"
                                />
                            ) : (
                                <p className="text-slate-800 font-semibold px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">{formData.last_name}</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Contact Number</label>
                        {isEditing ? (
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                    <Phone className="h-4 w-4" />
                                </div>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all font-medium text-slate-800"
                                />
                            </div>
                        ) : (
                            <p className="text-slate-800 font-semibold px-4 py-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                                <Phone className="h-4 w-4 text-slate-400" />
                                {formData.phone || 'Not provided'}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Email Address (Read Only)</label>
                        <p className="text-slate-500 font-semibold px-4 py-3 bg-slate-100 rounded-xl border border-slate-200 flex items-center gap-3">
                            <Mail className="h-4 w-4 text-slate-400" />
                            {user?.email}
                        </p>
                        <p className="text-xs text-slate-400 mt-2 ml-1">Contact your administrator or support to change your email address.</p>
                    </div>

                    {isEditing && (
                        <div className="pt-6 flex gap-3 border-t border-slate-100 mt-8">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setFormData({
                                        first_name: user?.name?.split(' ')[0] || '',
                                        last_name: user?.name?.split(' ').slice(1).join(' ') || '',
                                        phone: user?.phone || '',
                                    });
                                }}
                                className="px-6 py-3 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all flex-1"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex-1 flex items-center justify-center gap-2"
                            >
                                {loading ? 'Saving...' : <><Save className="h-4 w-4" /> Save Changes</>}
                            </button>
                        </div>
                    )}
                </form>

                {!isEditing && (
                    <div className="mt-12 pt-8 border-t border-slate-100">
                        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <KeyRound className="h-5 w-5 text-slate-400" />
                            Security Settings
                        </h3>
                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 flex items-center justify-between">
                            <div>
                                <p className="font-bold text-slate-800">Account Password</p>
                                <p className="text-sm text-slate-500 mt-0.5">Need to update your password? We will email you a secure reset link.</p>
                                {resetMessage && (
                                    <p className={`text-sm mt-3 font-medium ${resetMessage.includes('sent') ? 'text-green-600' : 'text-red-500'}`}>
                                        {resetMessage}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={handlePasswordReset}
                                disabled={resetLoading}
                                className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-100 transition-all text-sm shrink-0 whitespace-nowrap shadow-sm ml-4"
                            >
                                {resetLoading ? 'Sending...' : 'Reset Password'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PersonalProfileTab;
